import { pushState } from '$app/navigation';
import { page } from '$app/state';
import { generateUUID } from '$lib/helper/uuid';
import type { Booking, BookingWithoutCustomer } from '$lib/model/booking';
import type { Giftcard } from '$lib/model/giftcard';
import {
	BookingPaymentMethod,
	BookingPaymentStatus,
	type BookingPayment
} from '$lib/model/payment';
import { apiStore } from '$lib/stores/api';
import {
	bookingGetTotalPrice,
	bookingIsHappening,
	bookingStore,
	getOfferPax
} from '$lib/stores/booking';
import { giftcardConsume } from '$lib/stores/giftcard';
import { t } from '$lib/stores/i18n';
import { offerStore } from '$lib/stores/offer';
import { now } from '$lib/stores/time';
import { toast } from '$lib/stores/toast';
import { get } from 'svelte/store';

export type PaymentSummary = {
	onlinePaid: number;
	onsitePaid: number;
	giftcardPaid: number;
	canceledAmount: number;
	totalPaid: number;
	remaining: number;
	total: number;
};

export function bookingGetPaymentSummary(booking: BookingWithoutCustomer): PaymentSummary {
	const payments = booking.payments.filter(
		(payment) =>
			payment.status === BookingPaymentStatus.SUCCESS ||
			payment.status === BookingPaymentStatus.REFUNDED
	);
	const total = bookingGetTotalPrice(booking);
	const onlinePaid = payments
		.filter((payment) => payment.method === BookingPaymentMethod.ONLINE)
		.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0);
	const onsitePaid = payments
		.filter((payment) => payment.method === BookingPaymentMethod.POS)
		.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0);
	const giftcardPaid = payments
		.filter((payment) => payment.method === BookingPaymentMethod.GIFTCARD)
		.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0);
	const canceledAmount = !bookingIsHappening(booking) ? bookingGetTotalPrice(booking) : 0;
	const totalPaid = onlinePaid + onsitePaid + giftcardPaid;
	const remaining = Math.round((total - canceledAmount - totalPaid) * 100) / 100;

	return {
		onlinePaid,
		onsitePaid,
		giftcardPaid,
		canceledAmount,
		totalPaid,
		remaining,
		total
	};
}

export function openPaymentTransactionHistory(booking: BookingWithoutCustomer) {
	pushState('', {
		...page.state,
		tab: 'paymentTransactionHistory',
		id: booking.id
	});
}

export function bookingGetTotalByMethod(booking: Booking, method: BookingPaymentMethod): number {
	return booking.payments
		.filter(
			(payment) =>
				payment.method === method && payment.status === BookingPaymentStatus.SUCCESS
		)
		.reduce((acc, payment) => acc + payment.amount - payment.amountRefunded, 0);
}

export function bookingIsMissingPrices(booking: BookingWithoutCustomer): boolean {
	const offers = booking.order.offers.map((offer) =>
		get(offerStore).find((o) => o.id === offer.offerId)
	);
	return (
		getOfferPax(booking) !== booking.pax &&
		!offers.every((offer) => offer?.guestPrices.filter((gp) => !gp.archivedAt).length === 0)
	);
}

export function payWithPos(booking: BookingWithoutCustomer, amount: number): Promise<void> {
	if (amount) bookingAddPayment(booking, amount, BookingPaymentMethod.POS);
	return Promise.resolve();
}

export function payWithGiftcard(
	booking: BookingWithoutCustomer,
	giftcard: Giftcard,
	amount: number
): Promise<void> {
	if (amount === 0) return Promise.resolve();
	return giftcardConsume(giftcard, amount).then(() => {
		bookingAddPayment(
			booking,
			amount,
			BookingPaymentMethod.GIFTCARD,
			(payment: BookingPayment) => {
				if (giftcard.id) payment.objectId = giftcard.id;
			}
		);
	});
}

export function payOnline(
	booking: BookingWithoutCustomer,
	amount: number,
	callback: (payment: BookingPayment) => void
): Promise<void> {
	if (amount === 0) return Promise.resolve();
	return new Promise((resolve, reject) => {
		bookingAddPayment(booking, amount, BookingPaymentMethod.ONLINE, callback);
	});
}

export function refundPosPayment(payment: BookingPayment): BookingPayment {
	if (
		payment.status !== BookingPaymentStatus.SUCCESS &&
		payment.status !== BookingPaymentStatus.REFUNDED
	)
		return payment;

	bookingRefundPayment(payment, payment.amount);
	return payment;
}

export function refundOnlinePayment(payment: BookingPayment, amount: number): BookingPayment {
	if (
		payment.status !== BookingPaymentStatus.SUCCESS &&
		payment.status !== BookingPaymentStatus.REFUNDED
	)
		return payment;

	bookingRefundPayment(payment, amount);
	return payment;
}

export function refundGiftcardPayment(payment: BookingPayment): BookingPayment {
	if (
		payment.status !== BookingPaymentStatus.SUCCESS &&
		payment.status !== BookingPaymentStatus.REFUNDED
	)
		return payment;

	bookingRefundPayment(payment, payment.amount);
	return payment;
}

export function cancelOnlinePayment(payment: BookingPayment): void {
	if (payment.status !== BookingPaymentStatus.PROCESSING) return;

	payment.status = BookingPaymentStatus.CANCELED;

	get(apiStore)
		?.put('/payment/' + payment.id + '/cancel', payment)
		.catch((e) => {
			payment.status = BookingPaymentStatus.PROCESSING;
			get(toast)({
				text: get(t)('error.payment.cancel', { error: e.response?.data?.error }),
				bgColor: 'error',
				icon: 'bang',
				fixed: true
			});
		})
		.then(() => {
			// Update booking store (we can be in the bookingForm or in the bookingList)
			const booking = get(bookingStore).find((b) => b.id === payment.bookingId);
			if (booking) {
				booking.payments = booking.payments.map((p) => (p.id === payment.id ? payment : p));
			}
		});
}

function bookingRefundPayment(payment: BookingPayment, amount: number) {
	if (payment.amountRefunded + amount > payment.amount) {
		amount = Math.round((payment.amount - payment.amountRefunded) * 100) / 100;
	}

	const amountRefunded = Math.round((payment.amountRefunded + amount) * 100) / 100;
	const currentStatus = payment.status;
	payment.amountRefunded = Math.round(amount * 100) / 100;
	payment.status = BookingPaymentStatus.REFUNDED;
	payment.updatedAt = get(now);

	get(apiStore)
		?.put('/payment/' + payment.id + '/refund', payment)
		.catch((e) => {
			payment.amountRefunded -= amount;
			payment.status = currentStatus;
			get(toast)({
				text: get(t)('error.refund-failed', { error: e.response?.data?.error }),
				bgColor: 'error',
				icon: 'bang',
				fixed: true
			});
		})
		.then(() => {
			// Update booking store (we can be in the bookingForm or in the bookingList)
			const booking = get(bookingStore).find((b) => b.id === payment.bookingId);
			if (booking) {
				booking.payments = booking.payments.map((p) => (p.id === payment.id ? payment : p));
			}
		});
	payment.amountRefunded = amountRefunded;
}

async function bookingAddPayment(
	booking: BookingWithoutCustomer,
	amount: number,
	method: BookingPaymentMethod,
	callback?: (payment: BookingPayment) => void
) {
	const payment: BookingPayment = {
		id: generateUUID(),
		bookingId: booking.id,
		amount: amount,
		method: method,
		status: amount > 0 ? BookingPaymentStatus.SUCCESS : BookingPaymentStatus.REFUNDED,
		createdAt: get(now),
		amountRefunded: 0,
		updatedAt: get(now),
		fee: 0
	};

	if (callback) callback(payment);

	get(apiStore)?.put('/payment/' + payment.id + '/pay', payment);

	booking.payments.push(payment);
}
